// Type guards and casting helpers for category-based record typing system

import type { BaseRecord, AnyRecord } from '../types/api';
import { getTableCategory } from './table-categories';
import type { Database } from '../types/database.types';

// Extract specific row types from the database schema
type AppUserRow = Database['core_crm']['Tables']['app_user']['Row'];
type TableInfoRow = Database['core_crm']['Tables']['table_info']['Row'];
type TableFieldRow = Database['core_crm']['Tables']['table_field']['Row'];
type ConstraintInfoRow = Database['core_crm']['Tables']['constraint_info']['Row'];
type AppGroupRow = Database['core_crm']['Tables']['app_group']['Row'];

// List of all known core_crm tables
export const CORE_CRM_TABLES = [
  'app_user',
  'table_info', 
  'table_field',
  'constraint_info',
  'publication_info',
  'table_publication',
  'picklist_field',
  'relationship_lookup',
  'search_history',
  'email_campaign_send',
  'app_group',
  'email_provider',
  'email_send',
  'email_template',
  'text_message_provider',
  'text_message_send',
  'text_message_template',
  'auth_event',
  'global_setting',
  'group_table_permissions',
  'app_group_member',
  'email_attachment',
  'preset_table_filter',
  'change_event',
  'picklist_value'
] as const;

export type CoreCrmTableName = typeof CORE_CRM_TABLES[number];

// Type guards for schema and table detection
export const isCoreSchema = (schema: string): schema is 'core_crm' => {
  return schema === 'core_crm';
};

export const isCoreTable = (schema: string, tableName: string): tableName is CoreCrmTableName => {
  return schema === 'core_crm' && CORE_CRM_TABLES.includes(tableName as CoreCrmTableName);
};

// Specific table type guards
export const isAppUserTable = (schema: string, tableName: string): boolean => {
  return schema === 'core_crm' && tableName === 'app_user';
};

export const isTableInfoTable = (schema: string, tableName: string): boolean => {
  return schema === 'core_crm' && tableName === 'table_info';
};

export const isTableFieldTable = (schema: string, tableName: string): boolean => {
  return schema === 'core_crm' && tableName === 'table_field';
};

export const isConstraintInfoTable = (schema: string, tableName: string): boolean => {
  return schema === 'core_crm' && tableName === 'constraint_info';
};

export const isAppGroupTable = (schema: string, tableName: string): boolean => {
  return schema === 'core_crm' && tableName === 'app_group';
};

// Generic typecast helper - routes to specific casting based on table
export const castToCoreType = <T extends BaseRecord>(
  data: BaseRecord | BaseRecord[] | null,
  schema: string,
  tableName: string
): T | T[] | null => {
  if (!data || !isCoreTable(schema, tableName)) {
    return data as T | T[] | null; // Stay as BaseRecord for user tables
  }

  // Route to specific casting based on table name
  switch (tableName) {
    case 'app_user':
      return data as T | T[] | null;
    case 'table_info':
      return data as T | T[] | null;
    case 'table_field':
      return data as T | T[] | null;
    case 'constraint_info':
      return data as T | T[] | null;
    case 'app_group':
      return data as T | T[] | null;
    default:
      return data as T | T[] | null;
  }
};

// Specific casting helpers for common tables
export const castToAppUsers = (data: BaseRecord[]): AppUserRow[] => {
  return data as AppUserRow[];
};

export const castToAppUser = (data: BaseRecord): AppUserRow => {
  return data as AppUserRow;
};

export const castToTableInfo = (data: BaseRecord[]): TableInfoRow[] => {
  return data as TableInfoRow[];
};

export const castToTableInfoSingle = (data: BaseRecord): TableInfoRow => {
  return data as TableInfoRow;
};

export const castToTableFields = (data: BaseRecord[]): TableFieldRow[] => {
  return data as TableFieldRow[];
};

export const castToTableField = (data: BaseRecord): TableFieldRow => {
  return data as TableFieldRow;
};

export const castToConstraintInfo = (data: BaseRecord[]): ConstraintInfoRow[] => {
  return data as ConstraintInfoRow[];
};

export const castToAppGroups = (data: BaseRecord[]): AppGroupRow[] => {
  return data as AppGroupRow[];
};

export const castToAppGroup = (data: BaseRecord): AppGroupRow => {
  return data as AppGroupRow;
};

// Category-aware smart casting that handles all table types properly
export const smartCast = <T extends AnyRecord = BaseRecord>(
  data: AnyRecord | AnyRecord[] | null,
  schema: string,
  tableName: string
): T | T[] | null => {
  if (!data) {
    return null;
  }

  const category = getTableCategory(tableName, schema);
  
  // For business data tables and user-defined tables, use specific typing if available
  if (category === 'business_data' || category === 'user_defined') {
    if (!isCoreTable(schema, tableName)) {
      return data as T | T[] | null;
    }

    // Auto-detect array vs single and cast appropriately for known core tables
    if (Array.isArray(data)) {
      switch (tableName) {
        case 'app_user':
          return castToAppUsers(data as BaseRecord[]) as unknown as T[] | null;
        case 'table_info':
          return castToTableInfo(data as BaseRecord[]) as unknown as T[] | null;
        case 'table_field':
          return castToTableFields(data as BaseRecord[]) as unknown as T[] | null;
        case 'constraint_info':
          return castToConstraintInfo(data as BaseRecord[]) as unknown as T[] | null;
        case 'app_group':
          return castToAppGroups(data as BaseRecord[]) as unknown as T[] | null;
        default:
          return data as T[] | null;
      }
    } else {
      switch (tableName) {
        case 'app_user':
          return castToAppUser(data as BaseRecord) as unknown as T | null;
        case 'table_info':
          return castToTableInfoSingle(data as BaseRecord) as unknown as T | null;
        case 'table_field':
          return castToTableField(data as BaseRecord) as unknown as T | null;
        case 'app_group':
          return castToAppGroup(data as BaseRecord) as unknown as T | null;
        default:
          return data as T | null;
      }
    }
  }

  // For system logs and analytics, return as appropriate type without additional casting
  return data as T | T[] | null;
};

// Helper to check if we can provide enhanced typing for a table
export const canProvideTyping = (schema: string, tableName: string): boolean => {
  return isCoreTable(schema, tableName);
};

// Type assertion helpers for common operations
export const assertAppUser = (data: BaseRecord): AppUserRow => {
  return data as AppUserRow;
};

export const assertTableInfo = (data: BaseRecord): TableInfoRow => {
  return data as TableInfoRow;
};

export const assertTableField = (data: BaseRecord): TableFieldRow => {
  return data as TableFieldRow;
};

// Helper for conditional typing in generic functions
export const withTyping = <T extends BaseRecord, R>(
  data: BaseRecord | BaseRecord[] | null,
  schema: string,
  tableName: string,
  callback: (typedData: T | T[] | null) => R
): R => {
  const typedData = smartCast<T>(data, schema, tableName);
  return callback(typedData);
};